/**
 * The Diamond Sutra — editorial authority essays, tracks 13–17.
 * Thematic analysis only. No recording facts, credits, durations, lyrics,
 * or streaming destinations are asserted here.
 */
import type { SongEssay } from "./types";

export const DIAMOND_SUTRA_ESSAYS_13_17: Record<string, SongEssay> = {
  create: {
    seoTitle: "Create — Buddha KA | The Diamond Sutra",
    seoDescription:
      "Creation treated as a law rather than a mood. Buddha KA examines making as the only honest proof of an interior — and why speaking a thing into existence is a discipline, not a wish.",
    standfirst:
      "The interior is only ever verified by what leaves it. Creation is the receipt.",
    sections: [
      {
        eyebrow: "About the song",
        title: "About the Song",
        paragraphs: [
          "“Create” arrives at the point in the album where examination has to produce something. Twelve tracks of dismantling leave a person clean and, if nothing follows, empty. The record refuses to end that way.",
          "The verb is bare on purpose. Not manifest, not attract, not receive — create. Each of the softer words places the effort somewhere else and leaves the person waiting. This one puts the weight back where the album has kept it the whole time: on the maker.",
          "The song's premise is that consciousness is not proven by insight. Insight is cheap and endlessly available. What a person actually builds is the only part of their interior that can be examined by anyone, including themselves.",
        ],
      },
      {
        eyebrow: "The inquiry",
        title: "Speaking It Into Existence, Precisely",
        paragraphs: [
          "The phrase is worn thin by repetition, and the track takes it seriously enough to reclaim it. Speech does construct: it commits, it recruits other people, it makes retreat expensive, and it converts a private idea into a public obligation.",
          "But it constructs nothing on its own. A declaration with no structure behind it is only a louder version of a wish, and the album has already argued that repetition is how a mind installs whatever it is fed — including its own comfortable lies.",
          "So the track draws the distinction sharply. A spoken thing becomes real when the speaking is followed by cost: hours, refusals, revision, and the unglamorous return to the work after the feeling that started it has gone.",
        ],
      },
      {
        eyebrow: "The mechanism",
        title: "Creation as a Lawful Act",
        paragraphs: [
          "The album's mechanics chapter established that interior life behaves lawfully — attention concentrates, consequence follows structure, formation precedes visibility. Creation is where those laws become visible from outside.",
          "This is why the track is unsentimental about inspiration. Inspiration selects the direction; it does not carry the load. The load is carried by whatever the person has built to hold it, which returns the argument to discipline rather than intensity.",
          "It also reframes failure. A made thing that does not work is information, and information is what the whole record has been asking for. A thing never made yields nothing at all — not even a correction.",
        ],
      },
      {
        eyebrow: "Context",
        title: "Inside The Diamond Sutra",
        paragraphs: [
          "Placed after the album's chapter on language and just before its chapter on release, this track is the hinge between examining the interior and spending it.",
          "It answers the record's earlier questions directly. If desire was inherited and perception was borrowed, then the one uncontestably owned thing left is what a person chooses to bring into existence with the time remaining.",
          "Read alongside the album's formation material, it completes a single argument: unseen work first, structure second, and then something that exists whether or not it was believed in.",
        ],
      },
      {
        eyebrow: "Listening",
        title: "What to Listen For",
        paragraphs: [
          "Listen for the absence of persuasion. The track does not try to motivate; it describes a requirement.",
          "Notice where the language turns concrete. Every time the song reaches for the specific instead of the elevated, it is making its case.",
          "Then make one small thing before the feeling passes. The song is only worth what it produces.",
        ],
      },
    ],
    companions: [
      {
        songSlug: "spiritual-mechanics",
        title: "Spiritual Mechanics",
        reason: "The lawfulness this track puts to work.",
      },
      {
        songSlug: "unleash-divine",
        title: "Unleash Divine",
        reason: "What making costs, once the capacity behind it is released.",
      },
    ],
  },

  "unleash-divine": {
    seoTitle: "Unleash Divine — Buddha KA | The Diamond Sutra",
    seoDescription:
      "From recognition to embodiment. Buddha KA on latent capacity, disciplined release, and why unleashing anything sacred is a question of structure rather than intensity.",
    standfirst:
      "Capacity that is recognised but never expressed becomes a weight rather than a gift.",
    sections: [
      {
        eyebrow: "About the song",
        title: "About the Song",
        paragraphs: [
          "“Unleash Divine” follows the album's turn toward making with the harder question: what a person is supposed to do with what is already in them.",
          "The verb is deliberate. Unleashing implies something already present and restrained — not acquired, not requested, but held back. The track's premise is that most of what people wait to receive they are actually withholding.",
          "It is also a demanding position, because it removes the excuse of insufficiency. If the capacity is latent rather than absent, the delay has to be accounted for differently.",
        ],
      },
      {
        eyebrow: "The mechanism",
        title: "Discipline as the Release Valve",
        paragraphs: [
          "Release without structure dissipates. Intensity that has nowhere to go produces exhaustion and calls it passion, which is one of the most common ways sincere people burn through their own capacity.",
          "What allows anything powerful to be unleashed safely is architecture: practice, form, restraint, and repetition that can carry force without breaking. The album's formation chapter established exactly this, and here it becomes the condition of expression.",
          "So the song reads discipline as the opposite of suppression. Structure is not what holds capacity back; it is what makes releasing it survivable.",
        ],
      },
      {
        eyebrow: "The idea",
        title: "Latent Does Not Mean Guaranteed",
        paragraphs: [
          "The track resists the flattering version of potential, in which capacity inevitably emerges given enough belief. Nothing in the album has suggested that consequence is automatic.",
          "Latent capacity is closer to raw material: real, valuable, and inert until something is done with it. It can also be spent — on distraction, on performance, on other people's frames.",
          "That is where the record's earlier chapters return. Energetic ownership is not a metaphor here. Capacity flows toward whatever holds attention, and whoever holds the attention effectively owns the output.",
        ],
      },
      {
        eyebrow: "Context",
        title: "Why It Matters in the Album",
        paragraphs: [
          "The Diamond Sutra spends its first two thirds removing borrowed material. This track addresses what stands in the space that clearing creates.",
          "It is the album's most active moment in the sense of demand: everything examined earlier is now the listener's responsibility rather than the record's.",
          "Three tracks remain after it, and each concerns what expression is aimed at, how high it can carry, and how its results should be measured.",
        ],
      },
      {
        eyebrow: "Listening",
        title: "What to Listen For",
        paragraphs: [
          "Listen for force under control. The interest is in containment, not in volume.",
          "Notice the difference between feeling activated and being organised. Only one of those survives the following morning.",
          "Then name one capacity you have been holding, and the structure that would let it out without wasting it.",
        ],
      },
    ],
    companions: [
      {
        songSlug: "create",
        title: "Create",
        reason: "The act this capacity is released into.",
      },
      {
        songSlug: "speak-life",
        title: "Speak Life",
        reason: "Expression at its most immediate — capacity released through language.",
      },
    ],
  },

  "field-of-dreams": {
    seoTitle: "Field of Dreams — Buddha KA | The Diamond Sutra",
    seoDescription:
      "Imagination examined as a working faculty. Buddha KA separates disciplined creation from fantasy and traces how internal models quietly become the boundaries of a lived life.",
    standfirst:
      "The interior model is not a daydream. It is the blueprint the life gets built against.",
    sections: [
      {
        eyebrow: "About the song",
        title: "About the Song",
        paragraphs: [
          "“Field of Dreams” treats imagination as a faculty with consequences rather than as escape. What a person can picture in detail sets the outer limit of what they will move toward, and most limits are imaginative before they are material.",
          "The image of a field is doing real work. A field is open, unbuilt, and indifferent — it will grow whatever is planted, which is generous and dangerous in equal measure.",
          "The song's tension is between that openness and the discipline required to use it. Possibility without direction produces nothing but weather.",
        ],
      },
      {
        eyebrow: "The distinction",
        title: "Creation and Fantasy",
        paragraphs: [
          "Both involve vivid internal imagery. The difference is what they are wired to.",
          "Fantasy is a closed loop: it delivers the emotional reward of the outcome without the cost, which is precisely why it reduces the likelihood of the outcome. It functions as a release valve for pressure that should have driven action.",
          "Disciplined imagination is open at the far end. It models the result and then works backward into requirements, constraints, and the next physical move. It is uncomfortable in a way fantasy never is, because it keeps returning the person to the gap.",
        ],
      },
      {
        eyebrow: "The mechanism",
        title: "Internal Models Become Boundaries",
        paragraphs: [
          "Perception filters for what the model considers possible. Opportunities outside it are frequently present and simply not registered as opportunities.",
          "This is where the album's earlier chapters on inherited seeing become practical. A model assembled from other people's expectations will filter a life down to their dimensions, and the filtering feels like realism from the inside.",
          "Expanding the model is therefore not motivational work. It is a perceptual correction with material consequences.",
        ],
      },
      {
        eyebrow: "Context",
        title: "Inside The Diamond Sutra",
        paragraphs: [
          "Late in the album, after the borrowed material has been named and the mechanics established, this track asks what the cleared interior is going to be used to build.",
          "It completes the arc begun in the formation chapter: roots first, then the structure they were always for.",
          "It also sets up the closing pair, which examine how high a person can actually be carried and how the results should be counted.",
        ],
      },
      {
        eyebrow: "Listening",
        title: "What to Listen For",
        paragraphs: [
          "Listen for the pull toward comfort. Any song about possibility offers an exit into pleasant imagining, and noticing the exit is the exercise.",
          "Notice which images arrive with a next step attached and which arrive as scenery.",
          "Then convert one image into a single physical action. That conversion is the entire distinction the song is drawing.",
        ],
      },
    ],
    companions: [
      {
        songSlug: "before-the-bloom",
        title: "Before the Bloom",
        reason: "The unseen formation that turns a model into something real.",
      },
      {
        songSlug: "different-math",
        title: "Different Math",
        reason: "How to count what the field actually produced.",
      },
    ],
  },

  elevate: {
    seoTitle: "Elevate — Buddha KA | The Diamond Sutra",
    seoDescription:
      "Ascent examined as a structural problem. Buddha KA on why altitude exposes whatever was never built properly, and why elevation is a change of standard rather than a change of mood.",
    standfirst:
      "Rising is not the difficult part. Holding the height without borrowing the person you were is.",
    sections: [
      {
        eyebrow: "About the song",
        title: "About the Song",
        paragraphs: [
          "“Elevate” takes the album's most commonly abused word and puts weight on it. In ordinary usage elevation describes a feeling — a lift, a good hour, a temporary altitude that arrives on its own and departs the same way.",
          "The track treats it as a structural condition instead. To be elevated is to be operating at a standard the previous version of the person could not sustain, which is measurable and considerably less comfortable than the feeling.",
          "That reframing is the song's whole argument. Anything that lifts a person without changing what they will tolerate has not elevated them; it has entertained them.",
        ],
      },
      {
        eyebrow: "The mechanism",
        title: "Altitude Exposes the Build",
        paragraphs: [
          "Height is diagnostic. At a low level, weak structure is invisible because nothing is testing it — habits, evasions, and unexamined agreements all pass without incident.",
          "Raise the level and the same weaknesses become load-bearing failures. More attention, more responsibility, and more consequence do not create new flaws; they reveal the ones that were already there and previously unbilled.",
          "This is why the album placed formation before expression. Elevation is not granted to whoever wants it most. It is survived by whoever built something capable of holding it, which is a slower and much quieter qualification.",
        ],
      },
      {
        eyebrow: "The idea",
        title: "Elevation Is a Change of Standard",
        paragraphs: [
          "The track's practical definition is narrow on purpose: a person has elevated when their new floor is higher than their old ceiling, and when returning to the old level now feels like a genuine cost rather than a relief.",
          "By that measure most reported transformation is mood, and mood reverts. A standard does not, because it operates through refusal — the things no longer entertained, the conversations no longer had, the compromises that stopped being available.",
          "There is a loss inside this that the song does not hide. Altitude removes company. Some relationships were held in place by a shared level, and raising it ends them without anyone behaving badly.",
        ],
      },
      {
        eyebrow: "Context",
        title: "Why It Matters in the Album",
        paragraphs: [
          "Positioned second to last, this is the record's summary of direction. Everything before it removed borrowed material, established mechanism, and released capacity; this track asks what all of that is supposed to raise.",
          "It also answers the album's opening. The record began with scattered attention being gathered; here that gathered attention is what makes any sustained altitude possible in the first place.",
          "It hands the closer a clean question: once a person is operating higher, by what equation do they judge whether it was worth it.",
        ],
      },
      {
        eyebrow: "Listening",
        title: "What to Listen For",
        paragraphs: [
          "Listen for lift that is earned rather than announced. Where the song builds, notice what it had to establish first.",
          "Notice your own response. A track about elevation is easy to enjoy as a feeling, and enjoying it that way is precisely the failure it describes.",
          "Then name one standard you would have to raise permanently, and what it would cost you this week.",
        ],
      },
    ],
    companions: [
      {
        songSlug: "before-the-bloom",
        title: "Before the Bloom",
        reason: "The unseen build that decides whether altitude can be held.",
      },
      {
        songSlug: "different-math",
        title: "Different Math",
        reason: "The equation that judges whether the climb was worth its price.",
      },
    ],
  },

  "different-math": {
    seoTitle: "Different Math — Buddha KA | The Diamond Sutra",
    seoDescription:
      "The album's closing argument on value. Buddha KA examines sacred accounting — the costs that never appear on a receipt and the equations that make a visibly successful life quietly unaffordable.",
    standfirst:
      "The question is never only what something returns. It is what it charges, and to which account.",
    sections: [
      {
        eyebrow: "About the song",
        title: "About the Song",
        paragraphs: [
          "“Different Math” closes The Diamond Sutra with a question about measurement. Every choice a person makes is priced by some equation, and almost nobody has read the one they are using.",
          "The default equation is inherited: visible gain, social position, speed, comparison. It is efficient and widely agreed upon, which is precisely why it goes unexamined. Agreement is not the same as accuracy.",
          "The song proposes a different accounting — one that registers costs the standard ledger has no column for, and returns that the standard ledger cannot see at all.",
        ],
      },
      {
        eyebrow: "The idea",
        title: "Sacred Accounting",
        paragraphs: [
          "Some costs never appear on a receipt: attention spent, integrity traded in increments, relationships deprioritised, capacity redirected into someone else's frame. They are real expenditures and they compound.",
          "Some returns are equally invisible: clarity, self-trust, the ability to hold a difficult truth without flinching, a life that does not require constant explanation.",
          "Sacred accounting simply insists these entries be recorded. Once they are, decisions that looked obviously profitable begin to look expensive, and vice versa.",
        ],
      },
      {
        eyebrow: "The mechanism",
        title: "Why the Equation Decides Everything",
        paragraphs: [
          "A person cannot outperform their measuring system. Effort applied through a flawed equation produces efficient movement toward the wrong outcome, and the more disciplined the person, the faster they arrive.",
          "This is why the album placed conditioning at its centre. An inherited equation quietly determines what will be pursued for decades, and it was rarely chosen consciously.",
          "Changing the equation is therefore more consequential than changing behaviour. Behaviour follows measurement, not the reverse.",
        ],
      },
      {
        eyebrow: "Context",
        title: "Closing The Diamond Sutra",
        paragraphs: [
          "As the final track, this is where the record's investigations settle. Perception was examined, thought was traced to its sources, desire was interrogated, mechanism was mapped, capacity was released, and altitude was tested — all of which is only usable once value is defined.",
          "The album's title metaphor lands here with its full weight. A clean cut removes what was never real, and much of what a standard ledger calls valuable does not survive that test.",
          "The record ends without instruction, which is consistent. The listener leaves holding an equation rather than an answer.",
        ],
      },
      {
        eyebrow: "Listening",
        title: "What to Listen For",
        paragraphs: [
          "Listen for finality without resolution. A closer that answered everything would contradict the album it ends.",
          "Notice which of your own recent decisions changes character when the invisible columns are filled in.",
          "Then run one real calculation. The song is only worth what it costs you to apply it.",
        ],
      },
    ],
    companions: [
      {
        songSlug: "spiritual-mechanics",
        title: "Spiritual Mechanics",
        reason: "The system this accounting measures.",
      },
      {
        songSlug: "who-taught-you-to-want",
        title: "Who Taught You to Want?",
        reason: "Where the inherited equation was installed in the first place.",
      },
    ],
  },
};
